/**
 * Host-aware façade. The UI calls `readContext()` / `insertResult()` and
 * never has to know which Office host is loaded.
 */

import type { OfficeHost } from './bootstrap';
import { readWordContext, insertAtCursor, replaceSelection, appendBelow } from './word';
import { readExcelContext, writeToSelection } from './excel';
import { readPowerPointContext, insertOnActiveSlide } from './powerpoint';

export type ContextScope = 'none' | 'selection' | 'document';

export interface DocumentContext {
  /** Compact human-readable summary used in the UI badge. */
  label: string;
  /** Plain-text payload to send to the agent. */
  text: string;
  /** Approx. token count (chars/4) used for the warn-when-large badge. */
  approxTokens: number;
  truncated: boolean;
}

export async function readContext(host: OfficeHost, scope: ContextScope): Promise<DocumentContext> {
  if (scope === 'none') {
    return { label: 'No document context', text: '', approxTokens: 0, truncated: false };
  }

  switch (host) {
    case 'Word': {
      const ctx = await readWordContext();
      const text = scope === 'selection' ? ctx.selection : ctx.body;
      const label =
        scope === 'selection'
          ? text
            ? `Selection · ${text.length.toLocaleString()} chars`
            : 'No selection'
          : `Whole document · ${ctx.charCount.toLocaleString()} chars${ctx.truncated ? ' (truncated)' : ''}`;
      return {
        label,
        text,
        approxTokens: Math.ceil(text.length / 4),
        truncated: scope === 'document' && ctx.truncated,
      };
    }
    case 'Excel': {
      const ctx = await readExcelContext();
      if (scope === 'selection') {
        return {
          label: `Selection ${ctx.selectionAddress}`,
          text: `# ${ctx.selectionAddress}\n${ctx.selectionTsv}`,
          approxTokens: Math.ceil(ctx.selectionTsv.length / 4),
          truncated: false,
        };
      }
      return {
        label: `${ctx.sheetName} · ${ctx.rowCount}×${ctx.colCount}${ctx.truncated ? ' (clamped)' : ''}`,
        text: `# Sheet: ${ctx.sheetName}\n# Range: ${ctx.usedRangeAddress}\n${ctx.usedRangeTsv}`,
        approxTokens: Math.ceil(ctx.usedRangeTsv.length / 4),
        truncated: ctx.truncated,
      };
    }
    case 'PowerPoint': {
      const ctx = await readPowerPointContext();
      const text = ctx.selectionText;
      return {
        label:
          scope === 'selection'
            ? text
              ? `Slide ${ctx.selectedSlideIndex || '?'} selection · ${text.length} chars`
              : 'No slide text selected'
            : 'PowerPoint full-deck context not yet supported',
        text,
        approxTokens: Math.ceil(text.length / 4),
        truncated: false,
      };
    }
    default:
      return { label: 'No host', text: '', approxTokens: 0, truncated: false };
  }
}

export type InsertMode = 'insert' | 'replace' | 'append';

export async function insertResult(
  host: OfficeHost,
  mode: InsertMode,
  text: string,
  html?: string,
): Promise<void> {
  switch (host) {
    case 'Word':
      if (mode === 'replace') return replaceSelection(text, html);
      if (mode === 'append') return appendBelow(text, html);
      return insertAtCursor(text, html);
    case 'Excel':
      // Excel ignores `mode` — always writes anchored at top-left of the
      // active selection (overwrites in place, which is the natural cell
      // behaviour for a spreadsheet user).
      return writeToSelection(text);
    case 'PowerPoint':
      return insertOnActiveSlide(text);
    default:
      return;
  }
}
